import BlockIcon from '@mui/icons-material/Block'; // Added import for block icon
import EngineeringIcon from '@mui/icons-material/Engineering';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import {
  Chip,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Can } from 'core/components/permissions/Can';
import { getFormattedRuPhone, isNumeric } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import { getRoleName } from 'core/utils/userUtils';
import useUser from 'hooks/swr/useUser';
import useUserDocuments from 'hooks/swr/useUserDocuments';
import CardedPage from 'layout/pages/CardedPage';
import { isNil } from 'lodash';
import { useId } from 'react';
import { useParams } from 'react-router-dom';
import UserPageSkeleton from './UserPageSkeleton';
import UserViewPageHeader from './UserViewPageHeader';

export default function UserViewPage(): JSX.Element {
  const { userId } = useParams();
  const { data: user, isLoading: isUserLoading } = useUser(
    +(userId ?? 0),
    isNumeric(userId ?? '')
  );
  const { data: userDocuments, isLoading: isUserDocsLoading } = useUserDocuments(
    +(userId ?? 0),
    isNumeric(userId ?? '')
  );
  const componentId = useId();

  const isLoading = isUserLoading || isUserDocsLoading;

  if (isLoading || isNil(user)) {
    return (
      <CardedPage title='Сотрудник' header={<UserViewPageHeader />}>
        <UserPageSkeleton />
      </CardedPage>
    );
  }

  return (
    <CardedPage
      title='Сотрудник'
      disablePadding
      header={<UserViewPageHeader user={user} />}>
      <List>
        {user.blocked && (
          <ListItem>
            <ListItemIcon>
              <BlockIcon color='error' />
            </ListItemIcon>
            <ListItemText
              primary={<Typography color='error'>Пользователь заблокирован</Typography>}
              secondary='Пользователь не может войти в систему и недоступен для выбора в списках'
            />
          </ListItem>
        )}
        <ListItem>
          <ListItemIcon>
            <PersonIcon />
          </ListItemIcon>
          <ListItemText primary='Роль' secondary={getRoleName(user.role)} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <PhoneIcon />
          </ListItemIcon>
          <ListItemText
            primary='Телефон'
            secondary={
              <Link href={`tel:+${user.phone}`}>{getFormattedRuPhone(user.phone)}</Link>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            <EngineeringIcon />
          </ListItemIcon>
          <ListItemText
            primary='Работает на этапах'
            secondaryTypographyProps={{ component: 'div' }}
            secondary={
              <Stack direction='row' flexWrap={'wrap'} gap={1}>
                {user.actionAccesses.map((a) => (
                  <Chip
                    key={`${componentId}-action-${a}`}
                    label={getOrderActionName(a)}
                  />
                ))}

                {user.actionAccesses.length === 0 && (
                  <Chip label='Нет доступа к этапам' variant='outlined' color='warning' />
                )}
              </Stack>
            }
          />
        </ListItem>
        <Can I='read' a='MangoUserSettings'>
          {user.mangoExtension !== '' && (
            <ListItem>
              <ListItemIcon>
                <HeadsetMicIcon />
              </ListItemIcon>
              <ListItemText
                primary='Mango Office'
                secondary={`Добавочный: ${user.mangoExtension}`}
              />
            </ListItem>
          )}
        </Can>
        <ListItem>
          <ListItemIcon>
            <InsertDriveFileIcon />
          </ListItemIcon>
          <ListItemText
            primary='Документы доступные в заказе'
            slotProps={{ secondary: { component: 'div' } }}
            secondary={
              <Stack direction='row' flexWrap={'wrap'} gap={1}>
                {userDocuments?.map((d) => (
                  <Chip key={`${componentId}-document-${d.id}`} label={d.name} />
                ))}

                {isNil(userDocuments) ||
                  (userDocuments.length === 0 && (
                    <Chip
                      label='Нет доступных документов'
                      variant='outlined'
                      color='warning'
                    />
                  ))}
              </Stack>
            }
          />
        </ListItem>
      </List>
    </CardedPage>
  );
}
